import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// PARTNERS → LICENCE CHECKS (Jesse, 2026-11-01, L4 A). Every open licence-check
// task, all partners: the 8:00 CT digest's list on screen — owner, reason, days
// waiting, oldest first (backend utils/licenceCheckAlerts openTasksAll).
//
// 🚨 NO "MARK DONE" HERE EITHER. A task clears when somebody records the reading
// on the partner's License verification panel (or the sweep sees it answered).
// Each row links straight to that panel.
// ─────────────────────────────────────────────────────────────────────────────

const TONE = {
  escalated: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b', label: 'Escalated' },
  reminded: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e', label: 'Reminded' },
  open: { bg: '#fff', border: '#e5e7eb', fg: '#374151', label: 'Open' },
};

function waited(r) {
  const b = r.business_days_waiting;
  const c = r.days_waiting;
  const bd = `${b} business day${b === 1 ? '' : 's'}`;
  return c !== b ? `${bd} (${c} calendar)` : bd;
}

export default function AdminLicenceChecks() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('all');
  const [loading, setLoading] = useState(true);
  // 🚨 doorly_admin_token (see pages/AdminPartnerTasks.jsx).
  const token = localStorage.getItem('doorly_admin_token');

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/licence-checks?scope=${scope}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d.error) setError(d.error); else { setData(d); setError(''); } })
      .catch(() => setError('Could not load the licence checks.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(load, [load]);

  const rows = (data && data.tasks) || [];

  return (
    <AdminLayout>
      <div style={{ padding: '22px 26px', maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 14, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>Licence checks</h1>
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'mine'].map((s) => (
              <button key={s} type="button" onClick={() => setScope(s)} data-testid={`licence-checks-scope-${s}`}
                style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 7,
                  cursor: 'pointer', border: '1px solid',
                  borderColor: scope === s ? '#111827' : '#d1d5db',
                  background: scope === s ? '#111827' : '#fff',
                  color: scope === s ? '#fff' : '#374151' }}>
                {s === 'all' ? 'All partners' : 'Partners I own'}
              </button>
            ))}
          </div>
        </div>

        <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6, margin: '6px 0 0' }}>
          Every licence a person still has to read, oldest first. A row clears when a check is
          recorded on the partner&rsquo;s License verification panel.
        </p>

        {error && <div style={{ marginTop: 14, fontSize: 12.5, color: '#b91c1c' }}>{error}</div>}
        {loading && !data && <div style={{ marginTop: 18, fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>}

        {data && !rows.length && !loading && (
          <div data-testid="licence-checks-empty" style={{ marginTop: 18, fontSize: 13, color: '#166534',
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9, padding: '12px 14px' }}>
            No licence checks are waiting.
          </div>
        )}

        {rows.length > 0 && (
          <table data-testid="licence-checks-table" style={{ marginTop: 14, width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                <th style={{ padding: '6px 8px' }}>Partner</th>
                <th style={{ padding: '6px 8px' }}>What to check</th>
                <th style={{ padding: '6px 8px' }}>Reason</th>
                <th style={{ padding: '6px 8px' }}>Owner</th>
                <th style={{ padding: '6px 8px' }}>Waiting</th>
                <th style={{ padding: '6px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const tone = r.escalated_at ? TONE.escalated : r.reminded_at ? TONE.reminded : TONE.open;
                return (
                  <tr key={r.id} data-testid={`licence-check-row-${r.partner_id}`}
                    style={{ background: tone.bg, borderTop: `1px solid ${tone.border}` }}>
                    <td style={{ padding: '9px 8px', fontWeight: 700, color: '#111827' }}>
                      {r.is_test ? <span style={{ color: '#6b7280', fontWeight: 600 }}>[TEST] </span> : null}
                      {r.name}
                      <div style={{ fontWeight: 500, color: '#6b7280' }}>{r.type_label}{r.state ? ` · ${r.state}` : ''}</div>
                    </td>
                    <td style={{ padding: '9px 8px', color: '#374151' }}>{r.what_to_check}</td>
                    <td style={{ padding: '9px 8px', color: '#374151' }}>{r.reason_label}</td>
                    <td style={{ padding: '9px 8px', color: r.owner_name ? '#374151' : '#991b1b' }}>
                      {r.owner_name || 'None on record'}
                    </td>
                    <td style={{ padding: '9px 8px', color: tone.fg, fontWeight: 700 }}>
                      {waited(r)}
                      {tone !== TONE.open && <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>{tone.label}</div>}
                    </td>
                    <td style={{ padding: '9px 8px' }}>
                      <a href={r.link} style={{ color: '#4338ca', fontWeight: 700 }}>Record a check</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
